import { useEffect, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";
import PeriodPicker, { periodQuery, todayWib } from "@/components/PeriodPicker";
import { Layers, PackageCheck, AlertTriangle, Receipt, TrendingUp, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { rp, num, pcs, pct, signed, fmtDate } from "@/lib/format";
import { Panel, Stat } from "@/components/common";
import WorkerActivityToday from "@/components/WorkerActivityToday";
import TopWipWidget from "@/components/TopWipWidget";
import RekonsiliasiList from "@/components/RekonsiliasiList";
import RijekReminderList from "@/components/RijekReminderList";
import { SampleReminderList } from "@/components/SamplePanel";
import { useAuth } from "@/context/AuthContext";

const greeting = () => { const h = (new Date().getUTCHours() + 7) % 24; return h < 11 ? "Selamat Pagi" : h < 15 ? "Selamat Siang" : h < 18 ? "Selamat Sore" : "Selamat Malam"; };

function KpiCard({ testId, icon: Icon, title, subtitle, value, secondary, tone }) {
  const tones = {
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  };
  return (
    <div data-testid={testId} className="bg-white rounded-[20px] p-4 sm:p-5 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_10px_30px_rgba(17,24,39,0.05)]">
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-md border flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-4.5 h-4.5" strokeWidth={2} />
        </div>
      </div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-3">{title}</div>
      <div className="text-[11px] text-slate-400">{subtitle}</div>
      <div className="mono text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1.5">{value}</div>
      {secondary && <div className="text-xs font-medium text-slate-500 mt-1">{secondary}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [pv, setPv] = useState({ period: "BULANAN", date: todayWib() });
  const [data, setData] = useState(null);
  const [graph, setGraph] = useState([]);

  useEffect(() => {
    const q = `?${periodQuery(pv)}`;
    api.get(`/dashboard/summary${q}`).then((r) => setData(r.data));
    api.get(`/dashboard/daily-graph${q}`).then((r) => setGraph(r.data.series));
  }, [pv]);

  if (!data) return <div className="text-slate-500">Memuat dashboard…</div>;
  const mr = data.month_running;
  const P = data.period?.period || "BULANAN";
  const L = { HARIAN: { card: "Hari Berjalan", ctrl: "Kontrol Hari Ini", todate: "Ringkasan Hari Ini", unit: "hari", graph: "Grafik Produksi — " }, MINGGUAN: { card: "Minggu Berjalan", ctrl: "Kontrol Hari Ini", todate: "Week To Date & Run Rate", unit: "minggu", graph: "Grafik Produksi Harian (7 hari) — " }, BULANAN: { card: "Month Running", ctrl: "Today's Control", todate: "Month To Date & Run Rate", unit: "bulan", graph: "Grafik Produksi Harian — " }, TAHUNAN: { card: "Tahun Berjalan", ctrl: "Kontrol Hari Ini", todate: "Year To Date & Run Rate", unit: "tahun", graph: "Grafik Produksi Bulanan — " }, CUSTOM: { card: "Rentang Berjalan", ctrl: "Kontrol Hari Ini", todate: "Ringkasan Rentang & Run Rate", unit: "hari", graph: "Grafik Produksi Harian — " } }[P];
  const donut = data.donut || { sedang_dijarit: 0, sudah_disetor: 0 };
  const donutTotal = donut.sedang_dijarit + donut.sudah_disetor;
  const donutData = [{ name: "Barang sedang di jarit", value: donut.sedang_dijarit, color: "#D97706" }, { name: "Barang sudah di setor", value: donut.sudah_disetor, color: "#059669" }];
  const c = data.cards;
  const tc = data.today_control;
  const mtd = data.month_to_date;
  const rr = data.run_rate;
  const netUp = tc.net_wip_change >= 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1" data-testid="dashboard-hero">
        <div>
          <div className="text-xs sm:text-sm text-slate-500" data-testid="dashboard-greeting">{greeting()}, <span className="data-raw text-slate-900">{user?.name}</span></div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-slate-900 mt-0.5" data-testid="dashboard-title">Dashboard Kontrol Produksi</h1>
        </div>
        <PeriodPicker value={pv} onChange={setPv} testId="dashboard-period" allowCustom />
      </div>

      {/* Produksi Jahitan Selesai (mengikuti periode terpilih) */}
      <div data-testid="finished-production-card" className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 sm:p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-testid="period-card-title">Produksi Jahitan Selesai</div>
            <div className="text-sm text-slate-600 mt-0.5" data-testid="period-card-label">
              {P === "HARIAN" ? "Tanggal" : P === "MINGGUAN" ? "Minggu (Sen–Min)" : P === "TAHUNAN" ? "Tahun" : "Bulan"}: <span className="font-semibold text-slate-900">{mr.label}</span>
              {P !== "HARIAN" && <span className="text-slate-400"> · {fmtDate(data.period.date_from)} – {fmtDate(data.period.date_to)}</span>} · WITA
            </div>
            <div className="mono text-3xl sm:text-4xl font-extrabold text-emerald-700 tracking-tight mt-2" data-testid="finished-good-total">{pcs(mtd.total_good)}</div>
            <div className="text-[11px] text-slate-400 mt-1">Good selesai disetor = Setor − Reject · hanya transaksi CONFIRMED</div>
          </div>
          <div className="grid grid-cols-2 gap-3 md:min-w-[320px]">
            <div className="px-1 py-1">
              <div className="text-xs text-slate-500">Total Setor</div>
              <div className="mono text-xl font-semibold text-slate-900" data-testid="finished-setor-total">{pcs(mtd.total_setor)}</div>
            </div>
            <div className="px-1 py-1">
              <div className="text-xs text-slate-500">Total Reject</div>
              <div className="mono text-xl font-semibold text-rose-700" data-testid="finished-reject-total">{pcs(mtd.total_reject)}</div>
              <div className="text-[11px] text-slate-400">{pct(mtd.reject_rate)} dari setor</div>
            </div>
          </div>
        </div>
      </div>

      <TopWipWidget />
      <WorkerActivityToday />
      <RekonsiliasiList />
      <RijekReminderList />
      <SampleReminderList />

      {/* 4 KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="summary-card-open-wip" icon={Layers} tone="amber"
          title="Open WIP" subtitle={c.wip_perlu_rekonsiliasi ? `PERLU REKONSILIASI · SETOR MELEBIHI AMBIL ${num(c.wip_selisih_rekonsiliasi)} PCS` : "Jahitan Keluar Belum Disetor"}
          value={pcs(c.open_wip)} secondary={c.wip_perlu_rekonsiliasi ? <span className="text-rose-700" data-testid="dashboard-wip-rekon">Saldo historis negatif — lihat panel PERLU REKONSILIASI</span> : undefined} />
        <KpiCard testId="summary-card-setor-today" icon={PackageCheck} tone="emerald"
          title={P === "HARIAN" ? "Setor" : "Setor Today"} subtitle={P === "HARIAN" ? "Jahitan Disetor Tanggal Ini" : "Jahitan Disetor Hari Ini"}
          value={pcs(c.setor_today)} />
        <KpiCard testId="summary-card-reject-today" icon={AlertTriangle} tone="rose"
          title={P === "HARIAN" ? "Reject" : "Reject Today"} subtitle={P === "HARIAN" ? "Cacat / Repair Tanggal Ini" : "Cacat / Repair Hari Ini"}
          value={pcs(c.reject_today)} secondary={`${pct(c.reject_today_rate)} dari total setor`} />
        <KpiCard testId="summary-card-bon-today" icon={Receipt} tone="indigo"
          title={P === "HARIAN" ? "Bon" : "Bon Today"} subtitle={P === "HARIAN" ? "Barang Pembantu Tanggal Ini" : "Barang Pembantu Hari Ini"}
          value={rp(c.bon_today_value)} secondary={`${num(c.bon_today_count)} transaksi`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Today's Control */}
        <Panel testId="panel-todays-control" title={L.ctrl} className="lg:col-span-4">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Ambil" value={pcs(tc.ambil)} tone="blue" />
            <Stat label="Setor" value={pcs(tc.setor)} tone="emerald" />
            <Stat label="Good" value={pcs(tc.good)} tone="emerald" />
            <Stat label="Reject" value={pcs(tc.reject)} tone="rose" />
            <Stat label="Bon" value={rp(tc.bon_value)} tone="indigo" />
          </div>
          <div className={`mt-4 rounded-md border px-4 py-3 flex items-center justify-between ${netUp ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Net WIP Change</div>
              <div className="text-[11px] text-slate-500">{netUp ? "WIP bertambah (Ambil > Setor)" : "WIP berkurang (Setor > Ambil)"}</div>
            </div>
            <div className={`mono text-xl font-extrabold flex items-center gap-1 ${netUp ? "text-amber-700" : "text-emerald-700"}`}>
              {netUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {signed(tc.net_wip_change, "PCS")}
            </div>
          </div>
        </Panel>

        {/* Month To Date + Run Rate */}
        <Panel testId="panel-month-to-date" title={L.todate} className="lg:col-span-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
            <Stat label="Total Keluar" value={pcs(mtd.total_ambil)} tone="blue" />
            <Stat label="Total Setor" value={pcs(mtd.total_setor)} tone="emerald" />
            <Stat label="Total Good" value={pcs(mtd.total_good)} tone="emerald" />
            <Stat label="Total Reject" value={pcs(mtd.total_reject)} sub={pct(mtd.reject_rate)} tone="rose" />
            <Stat label="Current Open WIP" value={pcs(data.current_open_wip)} tone="amber" sub={data.wip_rekonsiliasi?.perlu ? `PERLU REKONSILIASI · SETOR MELEBIHI AMBIL ${pcs(data.wip_rekonsiliasi.selisih)}` : undefined} />
            <Stat label="Total Bon" value={rp(mtd.total_bon)} tone="indigo" />
            <Stat label="Gross Ongkos Jahit" value={rp(mtd.gross_sewing_pay)} />
            <Stat label="Net Payable" value={rp(mtd.net_payable)} />
            <Stat label="Outstanding Payable" value={rp(mtd.outstanding_payable)} tone="rose" />
          </div>
          {P !== "HARIAN" && <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Avg Keluar / Hari" value={pcs(rr.avg_ambil)} />
            <Stat label="Avg Setor / Hari" value={pcs(rr.avg_setor)} />
            <Stat label="Avg Good / Hari" value={pcs(rr.avg_good)} />
            <div data-testid="projection-block">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Proyeksi Akhir {P === "MINGGUAN" ? "Minggu" : P === "TAHUNAN" ? "Tahun" : "Bulan"}</div>
              <div className="mono text-lg font-bold text-slate-900 mt-0.5">{pcs(rr.projected_month_end_setor)}</div>
              <div className="text-[10px] text-slate-400">RUN RATE PROJECTION</div>
            </div>
          </div>}
        </Panel>

        <Panel testId="panel-donut" title={`Sedang di Jarit vs Sudah di Setor — ${mr.label}`} className="lg:col-span-12">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="h-56 w-56 shrink-0">
              {donutTotal > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart><Pie data={donutData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2} stroke="#fff">
                    {donutData.map((e) => <Cell key={e.name} fill={e.color} />)}</Pie>
                    <Tooltip formatter={(v, n) => [`${num(v)} PCS`, n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} /></PieChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-sm text-slate-400 text-center">Belum ada transaksi pada periode ini</div>}
            </div>
            <div className="space-y-3 text-sm" data-testid="donut-legend">
              {donutData.map((e) => (
                <div key={e.name} className="flex items-center gap-3">
                  <span className="w-3.5 h-3.5 rounded-sm shrink-0" style={{ background: e.color }} />
                  <span className="text-slate-700 w-48">{e.name}</span>
                  <span className="mono font-bold text-slate-900">{num(e.value)} PCS</span>
                  <span className="mono text-slate-500">{donutTotal ? ((e.value / donutTotal) * 100).toFixed(1).replace(".", ",") : "0,0"}%</span>
                </div>
              ))}
              {donut.note && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="donut-note">{donut.note} (Ambil − Setor periode ini = {num(donut.wip_raw)} PCS)</div>}
              <div className="text-[11px] text-slate-400">Sumber: transaksi CONFIRMED periode terpilih — sedang di jarit = Ambil − Setor (jika positif), sudah di setor = total Setor.</div>
            </div>
          </div>
        </Panel>

        {/* Daily production graph */}
        <Panel testId="panel-daily-production-graph" title={`${L.graph}${mr.label}`} className="lg:col-span-12">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graph} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n) => [v == null ? "-" : num(v) + " PCS", n]}
                  labelFormatter={(l) => P === "TAHUNAN" ? `Bulan ${l}` : `Tanggal ${l}`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="ambil" name="Ambil" stroke="#2563EB" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="setor" name="Setor" stroke="#059669" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="good" name="Good" stroke="#10B981" strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />
                <Line type="monotone" dataKey="reject" name="Reject" stroke="#E11D48" strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}
